import React, { useEffect, useState } from 'react'
import { BsSearch, BsX } from 'react-icons/bs'
import { ESelect } from '../../Select/ESelect';
import { EBadge } from '../../EBadge/EBadge';
import { useDirectusApi } from '../../../../data-access';
import { EButton } from '../../Button/EButton';

export interface IAvailableFilters {
    search?: boolean
    centerId?: boolean;
    include?: boolean
    disease_case?: boolean
}
export type TPayload = {
    [key in keyof IAvailableFilters]: any;
};
interface IProps {
    filter: TPayload | undefined
    onChangeFilter: ({ key, value }: { key: keyof TPayload, value: any }) => void
    onSubmitFilter: () => void
    onClearFilter: () => void
    allowedFilters?: IAvailableFilters
}

const INCLUSION_OPTIONS = [
    { label: 'Included', value: 'included' },
    { label: 'Excluded', value: 'excluded' },
];

const DISEASE_OPTIONS = [
    { label: 'SGS', title: 'Symptomatic Gallstone', value: 'symptomatic_gallstone' },
    { label: 'AGS', title: 'Asymptomatic Gallstone', value: 'asymptomatic_gallstone' },
    { label: 'GBC', title: 'Gallbladder Cancer', value: 'gallbladder_cancer' },
    { label: 'NGC', title: 'Non Gallstone Control', value: 'non_gallstone_control' },
];

export default function Filter({ filter, onChangeFilter, onSubmitFilter, onClearFilter, allowedFilters }: IProps) {
    const [centerList, setCenterList] = useState<any[]>([])
    const { fetchDirectData } = useDirectusApi()

    useEffect(() => {
        if (allowedFilters?.centerId) {
            getCenterList()
        }
    }, [allowedFilters?.centerId])

    const getCenterList = async () => {
        const centersRes = await fetchDirectData("items/center_profile?filter[status]=published&fields=center_name,user_id.id");
        setCenterList(centersRes.data)
    }

    const handleInclusionPill = (value: string) => {
        onChangeFilter({ key: 'include', value: filter?.include === value ? undefined : value });
    };

    const handleDiseasePill = (value: string) => {
        onChangeFilter({ key: 'disease_case', value: filter?.disease_case === value ? undefined : value });
    };

    const activeDisease: string = filter?.disease_case ?? '';

    const hasActiveFilters = filter?.include || !!activeDisease || filter?.centerId || (filter?.search?.length > 2);

    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 my-3">

            {/* Search */}
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 h-8 shadow-sm focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 transition-all min-w-[180px] flex-1">
                <BsSearch className="text-gray-400 shrink-0" size={13} />
                <input
                    type="text"
                    placeholder="Search by name"
                    className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400 w-full"
                    value={filter?.search ?? ''}
                    onChange={(e) => onChangeFilter({ key: 'search', value: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && onSubmitFilter()}
                />
                {filter?.search && (
                    <button type="button" onClick={() => onChangeFilter({ key: 'search', value: undefined })} className="text-gray-300 hover:text-gray-500">
                        <BsX size={14} />
                    </button>
                )}
            </div>

            {/* Center */}
            {allowedFilters?.centerId && (
                <div className="w-44 shrink-0">
                    <ESelect
                        value={filter?.centerId}
                        onChange={(e) => onChangeFilter({ key: 'centerId', value: e as any })}
                        data={centerList?.map((c: any) => ({ label: c.center_name, value: c.user_id.id })) || []}
                        placeholder="Center"
                        clearable
                    />
                </div>
            )}

            {/* Divider */}
            {allowedFilters?.include && <div className="h-5 w-px bg-gray-200 hidden sm:block" />}

            {/* Inclusion — single select */}
            {allowedFilters?.include && (
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</span>
                    {INCLUSION_OPTIONS.map((opt) => (
                        <EBadge
                            key={opt.value}
                            label={opt.label}
                            isActive={filter?.include === opt.value}
                            onClick={() => handleInclusionPill(opt.value)}
                            variant="blue"
                        />
                    ))}
                </div>
            )}

            {/* Divider */}
            {allowedFilters?.disease_case && <div className="h-5 w-px bg-gray-200 hidden sm:block" />}

            {/* Disease Case — multi select with abbreviations */}
            {allowedFilters?.disease_case && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">Disease</span>
                    {DISEASE_OPTIONS.map((opt) => (
                        <EBadge
                            key={opt.value}
                            label={opt.label}
                            title={opt.title}
                            isActive={activeDisease === opt.value}
                            onClick={() => handleDiseasePill(opt.value)}
                            variant="emerald"
                            className="font-bold px-2.5"
                        />
                    ))}
                </div>
            )}

            {/* Clear all */}
            {hasActiveFilters && (
                <button
                    type="button"
                    onClick={() => {
                        onChangeFilter({ key: 'include', value: undefined });
                        onChangeFilter({ key: 'disease_case', value: undefined });
                        onChangeFilter({ key: 'centerId', value: undefined });
                        onChangeFilter({ key: 'search', value: undefined });
                        onClearFilter();
                    }}
                    className="flex items-center gap-0.5 text-xs text-gray-400 hover:text-red-500 font-medium transition-colors cursor-pointer whitespace-nowrap ml-1"
                >
                    <BsX size={14} /> Clear
                </button>
            )}

            <EButton
                variant="primary"
                size="sm"
                disabled={!hasActiveFilters}
                onClick={() => {
                    onSubmitFilter();
                }}
            >
                <BsSearch className="" /> Search
            </EButton>


        </div>
    )
}
